import { supabase, TABLES, handleSupabaseError, handleSupabaseSuccess } from '../lib/supabase'

export const loginUser = async (username, password) => {
  try {
    const { data: verificationResult, error: verifyError } = await supabase
      .rpc('verify_user_password', {
        input_username: username,
        input_password: password
      })

    if (verifyError) {
      console.error('Password verification error:', verifyError)
      return handleSupabaseError({ message: 'Authentication service error' })
    }

    if (!verificationResult || verificationResult.length === 0 || !verificationResult[0].is_valid) {
      return handleSupabaseError({ message: 'Invalid username or password' })
    }

    const user = verificationResult[0]

    const { data: fullUser, error: userError } = await supabase
      .from(TABLES.USERS)
      .select('*')
      .eq('id', user.user_id)
      .single()

    if (userError || !fullUser) {
      return handleSupabaseError({ message: 'User not found' })
    }

    // Update last login time
    await supabase
      .from(TABLES.USERS)
      .update({ last_login: new Date().toISOString() })
      .eq('id', fullUser.id)

    // Store authentication data in localStorage
    localStorage.setItem('sri_velmurugan_auth', 'true')
    localStorage.setItem('sri_velmurugan_user', JSON.stringify(fullUser))
    localStorage.setItem('sri_velmurugan_session_start', new Date().toISOString())

    return handleSupabaseSuccess(fullUser)
  } catch (error) {
    console.error('Login error:', error)
    return handleSupabaseError({ message: 'Login failed' })
  }
}

export const logoutUser = async () => {
  try {
    // Clear all authentication data
    localStorage.removeItem('sri_velmurugan_auth')
    localStorage.removeItem('sri_velmurugan_user')
    localStorage.removeItem('sri_velmurugan_session_start')
    
    return handleSupabaseSuccess({ message: 'Logged out successfully' })
  } catch (error) {
    return handleSupabaseError(error)
  }
}

export const getCurrentUser = () => {
  try {
    const isAuthenticated = localStorage.getItem('sri_velmurugan_auth') === 'true'
    const userStr = localStorage.getItem('sri_velmurugan_user')
    const sessionStart = localStorage.getItem('sri_velmurugan_session_start')
    
    if (isAuthenticated && userStr && sessionStart) {
      // Check if session is not too old (24 hours)
      const sessionStartTime = new Date(sessionStart)
      const currentTime = new Date()
      const hoursDiff = (currentTime - sessionStartTime) / (1000 * 60 * 60)
      
      if (hoursDiff < 24) {
        return JSON.parse(userStr)
      } else {
        // Session expired, clear data
        localStorage.removeItem('sri_velmurugan_auth')
        localStorage.removeItem('sri_velmurugan_user')
        localStorage.removeItem('sri_velmurugan_session_start')
        return null
      }
    }
    
    return null
  } catch (error) {
    console.error('Error getting current user:', error)
    // Clear corrupted data
    localStorage.removeItem('sri_velmurugan_auth')
    localStorage.removeItem('sri_velmurugan_user')
    localStorage.removeItem('sri_velmurugan_session_start')
    return null
  }
}

export const isAuthenticated = () => {
  try {
    const authToken = localStorage.getItem('sri_velmurugan_auth')
    const userStr = localStorage.getItem('sri_velmurugan_user')
    const sessionStart = localStorage.getItem('sri_velmurugan_session_start')
    
    if (authToken === 'true' && userStr && sessionStart) {
      // Check if session is not too old (24 hours)
      const sessionStartTime = new Date(sessionStart)
      const currentTime = new Date()
      const hoursDiff = (currentTime - sessionStartTime) / (1000 * 60 * 60)
      
      if (hoursDiff < 24) {
        return true
      } else {
        // Session expired, clear data
        localStorage.removeItem('sri_velmurugan_auth')
        localStorage.removeItem('sri_velmurugan_user')
        localStorage.removeItem('sri_velmurugan_session_start')
        return false
      }
    }
    
    return false
  } catch (error) {
    console.error('Error checking authentication:', error)
    return false
  }
}

export const verifyCredentials = async (username, password) => {
  try {
    const { data: verificationResult, error } = await supabase
      .rpc('verify_user_password', {
        input_username: username,
        input_password: password
      })

    if (error || !verificationResult || verificationResult.length === 0) {
      return { valid: false, message: 'Invalid username or password' }
    }

    const result = verificationResult[0]
    if (result.is_valid) {
      return { valid: true, user: result }
    }

    return { valid: false, message: 'Invalid username or password' }
  } catch (error) {
    return { valid: false, message: 'Authentication error' }
  }
}

// Function to refresh session (can be called periodically)
export const refreshSession = () => {
  try {
    const user = getCurrentUser()
    if (user) {
      // Update session start time
      localStorage.setItem('sri_velmurugan_session_start', new Date().toISOString())
      return true
    }
    return false
  } catch (error) {
    console.error('Error refreshing session:', error)
    return false
  }
}
